<template>
    <div :style="{ height: '100%' }">
        <busy-modal :show="propertyLoading" :overlay="true"></busy-modal>
        <grid
            ref="grid"
            v-show="!showOwnerPropertyForm"
            :columnDefs="columnDefs"
            :fetchCallback="fetchCallback"
        >
            <responsive-button
                :icon="['fas', 'arrows-rotate']"
                @click="refresh"
                variant="primary"
            >Refresh</responsive-button>
            <responsive-button
                :icon="['fas', 'plus']"
                variant="primary"
                @click="handleCreateProperty"
            >Add Property</responsive-button>
        </grid>
        <div v-if="showOwnerPropertyForm">
            <b-row class="mb-3">
                <b-col>
                    <responsive-button
                        :icon="['fas', 'arrow-left']"
                        @click="showOwnerPropertyForm = false"
                        variant="primary"
                    >Back to Properties</responsive-button>
                </b-col>
            </b-row>
            <owner-property-form
                v-if="showOwnerPropertyForm"
                :initial-values="propertyFormInitialValues"
                @success="handlePropertyFormSuccess"
            ></owner-property-form>
        </div>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import API from '../../../../api';
    import BusyModal from '../../../generic/busy-modal/busy-modal.vue';
    import Grid from '../../../generic/grid/grid.vue';
    import ResponsiveButton from '../../../generic/responsive-button/responsive-button.vue';
    import OwnerPropertyForm from './owner-property-form.vue';
    import ActionsCellRenderer from './actions-cell-renderer.vue';

    export default {
        name: 'OwnerEditProperties',
        components: {
            BusyModal,
            Grid,
            ResponsiveButton,
            OwnerPropertyForm,
            actionsCellRenderer: ActionsCellRenderer,
        },
        props: {
            owner: Object,
        },
        computed: {
            ...mapGetters('header', ['userCurrencySymbol']),
            fetchCallback() {
                return ({ offset, limit, sort, desc, search }) => {
                    this.offset = offset;
                    this.limit = limit;
                    this.sort = sort;
                    this.desc = desc;
                    this.search = search;
                    return API.get('/app/owner/owner-property/grid/', {
                        params: {
                            offset,
                            limit,
                            sort,
                            desc,
                            search,
                            owner: this.owner.id,
                        }
                    });
                };
            },
        },
        data() {
            return {
                propertyLoading: false,
                propertyFormInitialValues: {},
                showOwnerPropertyForm: false,
                columnDefs: [
                    {
                        field: 'order',
                        headerName: '',
                        sort: 'asc',
                        maxWidth: 80,
                    },
                    {
                        field: 'name',
                        headerName: 'Name',
                        minWidth: 200,
                    },
                    {
                        headerName: 'Status',
                        valueGetter: ({ data }) =>
                            data
                                ? data.deleted
                                    ? 'Deleted'
                                    : data.disabled
                                        ? 'Disabled'
                                        : 'Active'
                                : '',
                        sortable: false,
                    },
                    {
                        headerName: 'Commission',
                        field: 'commission',
                        minWidth: 130,
                        cellRenderer: ({ data, value }) =>
                            (data.is_flat ? this.userCurrencySymbol : '') +
                            parseFloat(value).toFixed(2).toString() +
                            (!data.is_flat ? '%' : ''),
                        sortable: false,
                    },
                    {
                        headerName: '',
                        minWidth: 150,
                        maxWidth: 150,
                        resizable: false,
                        cellStyle: { textAlign: 'center' },
                        cellRenderer: 'actionsCellRenderer',
                        cellRendererParams: {
                            onEdit: this.handleEditProperty,
                            onDelete: this.handleDeleteProperty,
                        }
                    },
                ],
                offset: 0,
                limit: 20,
                sort: 'name',
                desc: false,
                search: '',
            }
        },
        methods: {
            refresh() {
                this.$refs.grid.refreshDataSource();
            },
            handlePropertyFormSuccess({ id }) {
                this.refresh();
                this.handleEditProperty({ id });
            },
            handleCreateProperty() {
                this.propertyFormInitialValues = { owner_id: this.owner.id, };
                this.showOwnerPropertyForm = true;
            },
            async handleEditProperty({ id }) {
                this.propertyFormInitialValues = {};
                this.propertyLoading = true;
                try {
                    const response = await API.get(`/app/owner/owner-property/${id}/`);
                    const property = response.data;
                    this.propertyFormInitialValues = {
                        id: property.id,
                        owner_id: this.owner.id,
                        rental_property_id: property.rental_property_id,
                        name: property.name,
                        commission_calculation: property.commission_calculation,
                        allow_owner_will_clean: property.allow_owner_will_clean,
                        assess_channel_commissions_to_owner: property.assess_channel_commissions_to_owner,
                        include_lodgix_commissions_in_channel_commissions: property.include_lodgix_commissions_in_channel_commissions,
                        display_guest_name_in_owner_statement: property.display_guest_name_in_owner_statement,
                        pass_cancellation_fees_to_owner: property.pass_cancellation_fees_to_owner,
                        pass_cancellation_fees_to_owner_mode: property.pass_cancellation_fees_to_owner_mode,
                        is_flat: property.is_flat,
                        rental_fee: property.rental_fee,
                        rental_fee_is_flat: property.rental_fee_is_flat,
                        rental_fee_include_taxes: property.rental_fee_include_taxes,
                        rental_fee_include_fees: property.rental_fee_include_fees,
                        rental_fee_name: property.rental_fee_name,
                        cleaning_fee: property.cleaning_fee,
                        cleaning_fee_is_flat: property.cleaning_fee_is_flat,
                        cleaning_fee_name: property.cleaning_fee_name,
                        other_fee: property.other_fee,
                        other_fee_is_flat: property.other_fee_is_flat,
                        other_fee_name: property.other_fee_name,
                        commissions_details: property.commissions_details,
                    }
                    this.showOwnerPropertyForm = true;
                } finally {
                    this.propertyLoading = false;
                }
            },
            async handleDeleteProperty({ id }) {
                this.propertyLoading = true;
                try {
                    await API.delete(`/app/owner/owner-property/${id}/`);
                    this.refresh();
                } finally {
                    this.propertyLoading = false;
                }
            }
        }
    }
</script>